import _indexOf from "lodash/indexOf";
import _each from "lodash/each";
import _first from "lodash/first";
import _last from "lodash/last";
import _escape from "lodash/escape";
import baustein from "baustein";
import rothko from "web/script/modules/rothko";
import userprofiler from "web/script/modules/userprofiler";

/**
 * Find the next sibling suggestion.
 * @param element
 * @param direction The order in which to traverse: 'previous' or 'next'
 * @returns {*}
 */
function findSiblingSuggestion(element, direction) {
    var key = direction === "previous" ? "previousElementSibling" : "nextElementSibling";
    var result = element[key];

    while (result && !result.classList.contains("auto-complete__suggestion")) {
        result = result[key];
    }

    return result;
}

export default baustein.register("auto-complete", {
    tagName: "ul",

    defaultOptions: {
        query: null,
        gender: "",
    },

    template: () =>
        import(
            /* webpackChunkName: "template-auto-complete" */ "templates/modules/search_input/auto_complete.jinja"
        ),

    // used by touch device to determine if it's still undergoing touchmove event
    touchMoved: false,

    init() {
        this.autoCompleteClient = new rothko.Rothko("modules/search_input/auto_complete");

        this.autoCompleteClient.on(
            "data",
            function(event, data) {
                if (this.isDestroyed()) {
                    return;
                }

                if (this.options.onlyBrand) {
                    // TODO add test
                    if (data.has_query_suggestions) {
                        data.has_query_suggestions = false;
                        data.query_suggestions = [];
                    }

                    if (data.has_profile_suggestions) {
                        data.profile_suggestions = data.profile_suggestions.filter(
                            (suggestion) => suggestion.profile_type === "Brand"
                        );
                    }
                }

                // only the client knows about the value of this attribute
                data.is_visible = this.getRenderContext().auto_complete.is_visible;

                this.setRenderContext({
                    auto_complete: data,
                });

                if (this.options.onlyBrand) {
                    // we highlight the first option for better UX
                    this.selectFirst();
                }
            }.bind(this)
        );
    },

    getInitialRenderContext() {
        return {
            auto_complete: {
                has_any_suggestions: false,
                has_profile_suggestions: false,
                has_category_suggestions: false,
                has_query_suggestions: false,
                query_suggestions: [],
                profile_suggestions: [],
                category_suggestions: [],
                is_visible: false,
            },
        };
    },

    setupEvents(add) {
        add("mouseenter", ".auto-complete__suggestion", this.onMouseEnter);
        add("mouseleave", ".auto-complete, .auto-complete--sc", this.onMouseLeave);
        add("click", ".auto-complete__suggestion", this.onSuggestionSelect);
        add("mousedown", ".auto-complete__suggestion", this.onSuggestionSelect);

        // the following events are used in combination to get `click` event work on touch devices
        add("touchend", ".auto-complete__suggestion", this.onTouchEnd);
        add("touchmove", ".auto-complete__suggestion", this.onTouchMove);
        add("touchstart", ".auto-complete__suggestion", this.onTouchStart);
    },

    /**
     * Handles the components options being changed. If query is falsey then the suggestions
     * list is populated with the users previous search terms. Otherwise the components data is
     * fetched using Rothko.
     */
    onOptionsChange() {
        if (!this.options.query && !this.options.onlyBrand) {
            var previousSearchTerms = userprofiler.getSearchTerms();
            var hasPreviousSearchTerms = !!previousSearchTerms.length;

            this.setRenderContext({
                auto_complete: {
                    has_any_suggestions: hasPreviousSearchTerms,
                    has_profile_suggestions: false,
                    has_category_suggestions: false,
                    has_query_suggestions: hasPreviousSearchTerms,
                    query_suggestions: previousSearchTerms.slice(0, 5).map(function(term) {
                        var escapedTerm = _escape(term);
                        return {
                            query: escapedTerm,
                            type: "query",
                        };
                    }),
                    profile_suggestions: [],
                    category_suggestions: [],
                },
            });

            return;
        }

        this.autoCompleteClient
            .fetch({
                query: this.options.query,
                gender: this.options.gender,
            })
            .catch(
                /* istanbul ignore next */
                function(err) {
                    console.error(err);
                }
            );
    },

    /**
     * Shows the component. Will only be visible if the list of suggestions is not empty.
     */
    show() {
        var context = this.getRenderContext();
        context.auto_complete.is_visible = true;
        this.setRenderContext(context);
    },

    /**
     * Hides the component.
     */
    hide() {
        var context = this.getRenderContext();
        context.auto_complete.is_visible = false;
        this.setRenderContext(context);
    },

    /**
     * Method used for tracking. Should return a boolean indicating whether
     * there are suggestions and if they are visible/shown to the user.
     */
    isShowingSuggestions() {
        const context = this.getRenderContext();
        return context.auto_complete.is_visible && context.auto_complete.has_any_suggestions;
    },

    /**
     * Makes the previous suggestion in the list active. If the is no previous suggestion then
     * all suggestions become inactive.
     * @returns {object|null}
     */
    selectPrevious() {
        var active = this._getActiveSuggestion();
        var prev = active ? findSiblingSuggestion(active, "previous") : null;
        return this._makeSuggestionActive(prev);
    },

    /**
     * Makes the next suggestion in the list active. If there is no next suggestion then all
     * suggestions become inactive
     * @returns {object|null}
     */
    selectNext() {
        var active = this._getActiveSuggestion();
        var next = active ? findSiblingSuggestion(active, "next") : null;
        return this._makeSuggestionActive(next);
    },

    /**
     * Makes the first suggestion in the list active.
     * @returns {object|null}
     */
    selectFirst() {
        return this._makeSuggestionActive(_first(this._getSuggestions()));
    },

    /**
     * Makes the last suggestion in the list active.
     * @returns {object|null}
     */
    selectLast() {
        return this._makeSuggestionActive(_last(this._getSuggestions()));
    },

    /**
     * Returns an object containing info about the currently active suggestion, or null if no
     * suggestion is currently active.
     * @returns {object|null} An object containing the `suggestion` and `index` keys.
     *                        If there is no active suggestion returns null.
     */
    getActiveSuggestion() {
        var active = this._getActiveSuggestion();
        var context = this.getRenderContext().auto_complete;
        var suggestions;
        var index;

        // ordering is different depending on whether we show categories
        if (context.suggest_categories) {
            suggestions = context.query_suggestions
                .concat(context.category_suggestions)
                .concat(context.no_icon_profile_suggestions);
        } else {
            suggestions = context.query_suggestions.concat(context.profile_suggestions);
        }

        if (!active) {
            return null;
        }

        index = this._getIndexOfSuggestion(active);

        return {
            suggestion: suggestions[index],
            index: index,
        };
    },

    /**
     * Handles mouseenter events
     * @param {Event} event
     * @param {HTMLElement} target
     */
    onMouseEnter(event, target) {
        this._makeSuggestionActive(target);
    },

    /**
     * Handles mouseleave events.
     * @param {Event} event
     */
    onMouseLeave(event) {
        if (event.target === this.el) {
            this._makeSuggestionActive(null);
        }
    },

    /**
     * Handles a suggestion being clicked on. Emits a `suggestionSelect` event which has the
     * same attributes as the object returned by `getActiveSuggestion`.
     * @param {Event} event
     * @param {HTMLElement} suggestion
     */
    onSuggestionSelect(event, suggestion) {
        event.preventDefault();
        this.emit("suggestionSelect", this._makeSuggestionActive(suggestion));
    },

    /**
     * Makes `suggestion` the active suggestion. If `suggestion` is null then all suggestions becomes inactive.
     * @param {HTMLElement} toMakeActive
     * @returns {object} An object representing the new active suggestion or null if no suggestion is active.
     * @private
     */
    _makeSuggestionActive(toMakeActive) {
        _each(this._getSuggestions(), function(suggestion) {
            suggestion.classList.toggle(
                "auto-complete__suggestion--active",
                suggestion === toMakeActive
            );
        });
        return this.getActiveSuggestion();
    },

    /**
     * Returns the index of the `suggestion` element.
     * @param {HTMLElement} suggestion
     * @returns {number}
     * @private
     */
    _getIndexOfSuggestion(suggestion) {
        return _indexOf(this._getSuggestions(), suggestion);
    },

    /**
     * Returns all current suggestion elements.
     * @returns {NodeList}
     * @private
     */
    _getSuggestions() {
        return this.el.querySelectorAll(".auto-complete__suggestion");
    },

    /**
     * Returns the currently active suggestion element or null.
     * @returns {Element}
     * @private
     */
    _getActiveSuggestion() {
        return this.el.querySelector(".auto-complete__suggestion--active");
    },

    /**
     * Used by touch device, calls onSuggestionSelect only when it's not scrolling
     */
    onTouchEnd(event, suggestion) {
        if (!this.touchMoved) {
            this.onSuggestionSelect(event, suggestion);
        }
    },

    /**
     * Used by touch device
     */
    onTouchMove() {
        this.touchMoved = true;
    },

    /**
     * Used by touch device
     */
    onTouchStart() {
        this.touchMoved = false;
    },
});
